import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus, FaEdit, FaTrashAlt, FaCheck, FaFileUpload, FaUndo, FaRobot, FaSearch, FaChartLine, FaPaperclip, FaTimes, FaDownload
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { ccvAPI, equipmentAPI, settingsAPI, aiAPI, samplingPlansAPI, commercialConfigAPI } from '../services/api';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import useApprovalRights from '../hooks/useApprovalRights';
import { Modal } from '../components/Modals';
import StatusBadge from '../components/StatusBadge';
import AIResultPanel from '../components/AIResultPanel';
import useAIAction from '../hooks/useAIAction';
import { ActionsMenu } from '../components/ActionsMenu';
import FilterStatCards, { approvalStatusGroups, matchesGroup } from '../components/FilterStatCards';
import { FaClipboardList, FaFileAlt } from 'react-icons/fa';
import TablePagination from '../components/TablePagination';

const getRows = (payload) => {
  if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

// Clamps a numeric result/limit to a fixed decimal count for display so scientific
// notation and long floating-point tails never leak into a CCV record; leaves
// non-numeric values (blank, qualitative text) untouched.
const fmtNum = (value, digits = 6) => {
  if (value === '' || value === null || value === undefined) return '-';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(digits);
};

const FREQUENCY_UNITS_BY_TYPE = {
  'Batch-based': ['Batches'],
  'Time-based': ['Days', 'Weeks', 'Months']
};

// Quick-pick parameters for a CCV criterion — pH/Conductivity/TOC are common rinse-quality
// checks (same set already offered as default criteria in SamplingPlans.js). "Custom" leaves
// the label free text for anything not covered here.
const PARAMETER_PRESETS = [
  { label: 'TOC', unit: 'ppm' },
  { label: 'Conductivity', unit: 'uS/cm' },
  { label: 'pH', unit: 'pH' },
  { label: 'Chemical Residue', unit: 'ppm' },
  { label: 'Bioburden', unit: 'cfu/mL' },
  { label: 'Custom', unit: '' }
];

const createCriterionRow = (overrides = {}) => ({
  ccv_criterion_id: null,
  criterion_label: '',
  limit_value: '',
  limit_unit: '',
  ...overrides
});

const EMPTY_PLAN = {
  ccv_plan_id: null,
  plan_code: '',
  equipment_id: '',
  plan_type: 'Time-based',
  frequency_value: '',
  frequency_unit: 'Months',
  remarks: '',
  criteria: [createCriterionRow({ criterion_label: 'pH', limit_unit: 'pH' })],
  required_locations: [],
  required_locations_rationale: ''
};

const EMPTY_RESULT = {
  ccv_result_id: null,
  ccv_plan_id: '',
  sampling_location_id: '',
  batch_number: '',
  sample_date: '',
  result_date: '',
  comments: '',
  attachment_name: '',
  attachment_data: '',
  values: []
};

// NMT (not-more-than) preview — mirrors backend/routes/ccv.js computeResultStatus so the
// operator sees PASS/FAIL before saving, not just after a round trip.
const previewResultStatus = (resultValue, limitValue) => {
  const value = Number(resultValue);
  const limit = Number(limitValue);
  if (!Number.isFinite(value) || !Number.isFinite(limit)) return '';
  return value <= limit ? 'PASS' : 'FAIL';
};

const RESULT_STATUS_STYLE = {
  PASS: { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' },
  FAIL: { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' }
};

const TREND_STYLE = {
  'Alert - Failure':     { bg: 'var(--danger-bg)',  color: 'var(--danger-text)',  border: 'var(--danger-border)' },
  'Trending Up - Review':{ bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' },
  'Trending Down':       { bg: 'var(--info-bg)',    color: 'var(--info-text)',    border: 'var(--info-border)' },
  Stable:                { bg: 'var(--surface-light)', color: 'var(--text-secondary)', border: 'var(--border-color)' },
  Baseline:              { bg: 'var(--surface-light)', color: 'var(--text-secondary)', border: 'var(--border-color)' }
};

const COMPLIANCE_STYLE = {
  Overdue:        { bg: 'var(--danger-bg)',  color: 'var(--danger-text)',  border: 'var(--danger-border)' },
  'Due Soon':     { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' },
  'On Track':     { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' },
  'Batch-based':  { bg: 'var(--info-bg)',    color: 'var(--info-text)',    border: 'var(--info-border)' },
  'No Data Yet':  { bg: 'var(--surface-light)', color: 'var(--text-secondary)', border: 'var(--border-color)' }
};

const Pill = ({ label, style }) => (
  <span style={{
    padding: '2px 10px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-xs)',
    background: style.bg, color: style.color, border: `1px solid ${style.border}`, whiteSpace: 'nowrap'
  }}>
    {label}
  </span>
);

// Time-based plans get a real due-date projection from the latest Approved result.
// Batch-based plans track "every N batches," which this app has no batch-production
// log to count against, so it's labeled rather than faked into a wrong due date.
const complianceStatusFor = (plan, results) => {
  if (plan.plan_type === 'Batch-based') return 'Batch-based';

  const approved = results
    .filter((r) => String(r.ccv_plan_id) === String(plan.ccv_plan_id) && r.status === 'Approved' && r.sample_date)
    .sort((a, b) => new Date(b.sample_date) - new Date(a.sample_date));

  if (!approved.length) return 'No Data Yet';

  const last = new Date(approved[0].sample_date);
  const due = new Date(last);
  const value = Number(plan.frequency_value) || 0;
  if (plan.frequency_unit === 'Weeks') due.setDate(due.getDate() + value * 7);
  else if (plan.frequency_unit === 'Months') due.setMonth(due.getMonth() + value);
  else due.setDate(due.getDate() + value);

  const daysLeft = Math.ceil((due - new Date()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return 'Overdue';
  if (daysLeft <= 7) return 'Due Soon';
  return 'On Track';
};

// Worst-of-all-parameters status for a sample event row (any FAIL wins, else PASS if every
// parameter has one, else '-' while results are still being entered).
const overallStatus = (values) => {
  if (!values?.length) return '-';
  if (values.some((v) => v.result_status === 'FAIL')) return 'FAIL';
  if (values.every((v) => v.result_status === 'PASS')) return 'PASS';
  return '-';
};

const canEditRecord = (row, approvalRequired) => (approvalRequired === false) || !row.status || row.status === 'Draft';

const readFileAsDataUrl = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => resolve('');
  reader.readAsDataURL(file);
});

const CCV = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [results, setResults] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [samplingLocations, setSamplingLocations] = useState([]);

  const [planSearch, setPlanSearch] = useState('');
  const [planCard, setPlanCard] = useState('All');
  const [resultCard, setResultCard] = useState('All');
  const [resultsPlanFilter, setResultsPlanFilter] = useState('');
  const [resultsSearch, setResultsSearch] = useState('');
  const [sortKey, setSortKey] = useState('sample_date');
  const [sortDir, setSortDir] = useState('desc');
  const [trendPlanId, setTrendPlanId] = useState('');
  const [trendCriterionId, setTrendCriterionId] = useState('');

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultForm, setResultForm] = useState(EMPTY_RESULT);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  const [esign, setEsign] = useState(null); // { kind: 'plan'|'result', mode, row, decision, password, comment }
  const [qmsLinked, setQmsLinked] = useState(false);
  const [changeRefGate, setChangeRefGate] = useState(null); // {kind, row} while the gate is open for an Approved row
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  const [pendingChangeRef, setPendingChangeRef] = useState(null);

  useEffect(() => {
    commercialConfigAPI.getRuntime().then((res) => {
      const integrations = res?.data?.data?.integrations || {};
      setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
    }).catch(() => {});
  }, []);
  const [esigning, setEsigning] = useState(false);
  const [planApprovalRequired, setPlanApprovalRequired] = useState(true);
  const [resultApprovalRequired, setResultApprovalRequired] = useState(true);

  const ai = useAIAction();
  const user = getCurrentUser();
  const role = String(user.role || '').trim().toLowerCase();
  const isSuperAdmin = role === 'super admin';
  const isAdmin = role === 'admin' || isSuperAdmin;
  const planRights = useApprovalRights('ccv_plan.approval');
  const resultRights = useApprovalRights('ccv_result.approval');

  const loadAll = async () => {
    try {
      setLoading(true);
      const [plansRes, resultsRes, equipRes, settingsRes, samplingRes] = await Promise.all([
        ccvAPI.getAll(), ccvAPI.getResults(), equipmentAPI.getAll(), settingsAPI.getAll(), samplingPlansAPI.getAll()
      ]);
      const loadedPlans = getRows(plansRes);
      setPlans(loadedPlans);
      setResults(getRows(resultsRes));
      setEquipment(getRows(equipRes));

      // Flatten every Approved Sampling Plan's locations — CCV results reference these so a
      // logged sample can say exactly which part it was drawn from (see backend/routes/ccv.js).
      const samplingPlansRows = getRows(samplingRes);
      const flatLocations = samplingPlansRows
        .filter((p) => p.status === 'Approved')
        .flatMap((p) => (p.locations || []).map((loc) => ({ ...loc, plan_code: p.plan_code })));
      setSamplingLocations(flatLocations);

      const settings = settingsRes?.data?.data || {};
      setPlanApprovalRequired(settings['workflow_approval_required_ccv_plan'] !== 'false');
      setResultApprovalRequired(settings['workflow_approval_required_ccv_result'] !== 'false');
      if (loadedPlans.length && !trendPlanId) {
        setTrendPlanId(String(loadedPlans[0].ccv_plan_id));
        setTrendCriterionId(String(loadedPlans[0].criteria?.[0]?.ccv_criterion_id || ''));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load CCV data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshPlans = async () => setPlans(getRows(await ccvAPI.getAll()));
  const refreshResults = async () => setResults(getRows(await ccvAPI.getResults()));

  const equipmentLabel = (equipmentId) => {
    const eq = equipment.find((e) => String(e.equipment_id) === String(equipmentId));
    return eq ? `${eq.equipment_code || ''} ${eq.equipment_name || ''}`.trim() : '-';
  };

  const filteredPlans = useMemo(() => {
    const q = planSearch.trim().toLowerCase();
    const base = plans.filter((p) => matchesGroup(approvalStatusGroups, planCard, p));
    if (!q) return base;
    return base.filter((p) =>
      (p.plan_code || '').toLowerCase().includes(q) ||
      equipmentLabel(p.equipment_id).toLowerCase().includes(q) ||
      (p.criteria || []).some((c) => (c.criterion_label || '').toLowerCase().includes(q))
    );
  }, [plans, planSearch, planCard, equipment]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Results: plan filter + free-text search (batch no. / plan / equipment / location) + sort ──
  const filteredResults = useMemo(() => {
    let rows = resultsPlanFilter ? results.filter((r) => String(r.ccv_plan_id) === String(resultsPlanFilter)) : results;
    rows = rows.filter((r) => matchesGroup(approvalStatusGroups, resultCard, r));
    const q = resultsSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        (r.batch_number || '').toLowerCase().includes(q) ||
        (r.plan_code || '').toLowerCase().includes(q) ||
        (r.equipment_name || '').toLowerCase().includes(q) ||
        (r.location_name || '').toLowerCase().includes(q) ||
        (r.location_code || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...rows].sort((a, b) => {
      let aVal, bVal;
      if (sortKey === 'batch_number') { aVal = a.batch_number || ''; bVal = b.batch_number || ''; }
      else if (sortKey === 'result_date') { aVal = a.result_date ? new Date(a.result_date).getTime() : 0; bVal = b.result_date ? new Date(b.result_date).getTime() : 0; }
      else if (sortKey === 'status') { aVal = overallStatus(a.values); bVal = overallStatus(b.values); }
      else { aVal = a.sample_date ? new Date(a.sample_date).getTime() : 0; bVal = b.sample_date ? new Date(b.sample_date).getTime() : 0; }
      if (typeof aVal === 'number' && typeof bVal === 'number') return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return sorted;
  }, [results, resultsPlanFilter, resultCard, resultsSearch, sortKey, sortDir]);

  const [planPage, setPlanPage] = useState(1);
  const [planPageSize, setPlanPageSize] = useState(25);
  const planTotalPages = Math.max(1, Math.ceil(filteredPlans.length / planPageSize));
  useEffect(() => { setPlanPage(1); }, [planSearch, planCard, planPageSize]);
  useEffect(() => { if (planPage > planTotalPages) setPlanPage(planTotalPages); }, [planPage, planTotalPages]);
  const paginatedPlans = filteredPlans.slice((planPage - 1) * planPageSize, planPage * planPageSize);

  const [resultPage, setResultPage] = useState(1);
  const [resultPageSize, setResultPageSize] = useState(25);
  const resultTotalPages = Math.max(1, Math.ceil(filteredResults.length / resultPageSize));
  useEffect(() => { setResultPage(1); }, [resultsSearch, resultCard, resultsPlanFilter, resultPageSize]);
  useEffect(() => { if (resultPage > resultTotalPages) setResultPage(resultTotalPages); }, [resultPage, resultTotalPages]);
  const paginatedResults = filteredResults.slice((resultPage - 1) * resultPageSize, resultPage * resultPageSize);

  const exportResultsToExcel = () => {
    if (!filteredResults.length) {
      toast.error('No CCV results to export');
      return;
    }
    const exportRows = filteredResults.map((r) => ({
      'Plan': r.plan_code,
      'Equipment': r.equipment_name || '-',
      'Location': r.location_name || '-',
      'Batch Number': r.batch_number || '-',
      'Sample Date': r.sample_date ? new Date(r.sample_date).toLocaleDateString() : '-',
      'Result Date': r.result_date ? new Date(r.result_date).toLocaleDateString() : '-',
      'Result Value': fmtNum(r.result_value),
      'Limit Value': fmtNum(r.limit_value),
      'Limit Unit': r.limit_unit || '-',
      'Status': r.result_status || '-',
      'Trend': r.trend_status || '-',
      'QA Approved By': r.qa_approved_by_name || '-'
    }));
    const wb = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, sheet, 'CCV Results');
    XLSX.writeFile(wb, `CCV_Results_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('CCV results exported to Excel');
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const sortIndicator = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // ── Plan CRUD ──────────────────────────────────────────────────────────────
  const openNewPlan = () => { setPlanForm(EMPTY_PLAN); setPlanModalOpen(true); };

  const openEditPlan = (plan) => {
    setPlanForm({
      ccv_plan_id: plan.ccv_plan_id,
      plan_code: plan.plan_code || '',
      equipment_id: plan.equipment_id ? String(plan.equipment_id) : '',
      plan_type: plan.plan_type || 'Time-based',
      frequency_value: plan.frequency_value ?? '',
      frequency_unit: plan.frequency_unit || (FREQUENCY_UNITS_BY_TYPE[plan.plan_type || 'Time-based'][0]),
      remarks: plan.remarks || '',
      criteria: (plan.criteria || []).length
        ? plan.criteria.map((c) => createCriterionRow(c))
        : [createCriterionRow()],
      required_locations: Array.isArray(plan.required_locations) ? plan.required_locations.map(String) : [],
      required_locations_rationale: plan.required_locations_rationale || ''
    });
    setPlanModalOpen(true);
  };

  const setPlanField = (field, value) => setPlanForm((prev) => {
    const next = { ...prev, [field]: value };
    if (field === 'plan_type') next.frequency_unit = FREQUENCY_UNITS_BY_TYPE[value][0];
    return next;
  });

  const setCriterionField = (index, field, value) => setPlanForm((prev) => ({
    ...prev,
    criteria: prev.criteria.map((c, i) => i !== index ? c : { ...c, [field]: value })
  }));

  const applyPreset = (index, presetLabel) => {
    const preset = PARAMETER_PRESETS.find((p) => p.label === presetLabel);
    if (!preset) return;
    setPlanForm((prev) => ({
      ...prev,
      criteria: prev.criteria.map((c, i) => i !== index ? c : {
        ...c,
        criterion_label: preset.label === 'Custom' ? '' : preset.label,
        limit_unit: preset.unit
      })
    }));
  };

  const addCriterionRow = () => setPlanForm((prev) => ({ ...prev, criteria: [...prev.criteria, createCriterionRow()] }));
  const removeCriterionRow = (index) => setPlanForm((prev) => ({ ...prev, criteria: prev.criteria.filter((_, i) => i !== index) }));

  const savePlan = async () => {
    if (!planForm.plan_code.trim()) return toast.warning('Plan Code is required');
    if (!planForm.equipment_id) return toast.warning('Equipment is required');
    const cleanCriteria = planForm.criteria.filter((c) => c.criterion_label.trim());
    if (!cleanCriteria.length) return toast.warning('At least one parameter (e.g. pH, Conductivity, TOC) is required');
    if ((planForm.required_locations || []).length > 0 && !(planForm.required_locations_rationale || '').trim()) {
      return toast.warning('A rationale is required when limiting this plan to a subset of locations');
    }

    setSaving(true);
    try {
      const payload = { ...planForm, equipment_id: planForm.equipment_id || null, criteria: cleanCriteria };
      if (planForm.ccv_plan_id) {
        await ccvAPI.update(planForm.ccv_plan_id, payload);
        toast.success('CCV plan updated');
      } else {
        await ccvAPI.create(payload);
        toast.success('CCV plan created');
      }
      await refreshPlans();
      setPlanModalOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save CCV plan');
    } finally {
      setSaving(false);
    }
  };

  // ── Result CRUD ────────────────────────────────────────────────────────────
  const openNewResult = (planId) => {
    const plan = plans.find((p) => String(p.ccv_plan_id) === String(planId));
    setResultForm({
      ...EMPTY_RESULT,
      ccv_plan_id: planId ? String(planId) : '',
      values: (plan?.criteria || []).map((c) => ({ ccv_criterion_id: c.ccv_criterion_id, result_value: '' }))
    });
    setResultModalOpen(true);
  };

  const openEditResult = (result) => {
    setResultForm({
      ccv_result_id: result.ccv_result_id,
      ccv_plan_id: result.ccv_plan_id ? String(result.ccv_plan_id) : '',
      sampling_location_id: result.sampling_location_id ? String(result.sampling_location_id) : '',
      batch_number: result.batch_number || '',
      sample_date: result.sample_date ? String(result.sample_date).slice(0, 10) : '',
      result_date: result.result_date ? String(result.result_date).slice(0, 10) : '',
      comments: result.comments || '',
      attachment_name: result.attachment_name || '',
      attachment_data: result.attachment_data || '',
      values: (result.values || []).map((v) => ({ ccv_criterion_id: v.ccv_criterion_id, result_value: v.result_value ?? '' }))
    });
    setResultModalOpen(true);
  };

  const setResultField = (field, value) => setResultForm((prev) => ({ ...prev, [field]: value }));

  const setResultValueField = (criterionId, value) => setResultForm((prev) => ({
    ...prev,
    values: prev.values.map((v) => String(v.ccv_criterion_id) === String(criterionId) ? { ...v, result_value: value } : v)
  }));

  const resultPlan = useMemo(
    () => plans.find((p) => String(p.ccv_plan_id) === String(resultForm.ccv_plan_id)) || null,
    [plans, resultForm.ccv_plan_id]
  );

  // Switching the plan resets the value rows to that plan's current parameter list.
  const handleResultPlanChange = (planId) => {
    const plan = plans.find((p) => String(p.ccv_plan_id) === String(planId));
    setResultForm((prev) => ({
      ...prev,
      ccv_plan_id: planId,
      sampling_location_id: '',
      values: (plan?.criteria || []).map((c) => ({ ccv_criterion_id: c.ccv_criterion_id, result_value: '' }))
    }));
  };

  const resultLocationOptions = useMemo(() => {
    if (!resultPlan) return [];
    const allForEquipment = samplingLocations.filter((loc) => String(loc.equipment_id) === String(resultPlan.equipment_id));
    const required = Array.isArray(resultPlan.required_locations) ? resultPlan.required_locations.map(String) : [];
    // A plan that has explicitly declared a required subset limits the dropdown to just
    // that subset — the reduction is enforced here, not just documented on the plan.
    return required.length ? allForEquipment.filter((loc) => required.includes(String(loc.location_id))) : allForEquipment;
  }, [samplingLocations, resultPlan]);

  const planEquipmentLocations = useMemo(() => {
    if (!planForm.equipment_id) return [];
    return samplingLocations.filter((loc) => String(loc.equipment_id) === String(planForm.equipment_id));
  }, [samplingLocations, planForm.equipment_id]);

  const togglePlanRequiredLocation = (locationId) => {
    setPlanForm((prev) => {
      const current = prev.required_locations || [];
      const idStr = String(locationId);
      const next = current.includes(idStr) ? current.filter((id) => id !== idStr) : [...current, idStr];
      return { ...prev, required_locations: next };
    });
  };

  const handleAttachmentChange = async (file) => {
    if (!file) return;
    setAttachmentUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setResultForm((prev) => ({ ...prev, attachment_name: file.name, attachment_data: dataUrl }));
    } finally {
      setAttachmentUploading(false);
    }
  };

  const saveResult = async () => {
    if (!resultForm.ccv_plan_id) return toast.warning('CCV Plan is required');
    if (!resultForm.sample_date) return toast.warning('Sample date is required');
    if (!resultForm.values.length) return toast.warning('This plan has no parameters configured yet');
    if (resultForm.values.some((v) => v.result_value === '' || v.result_value === null)) {
      return toast.warning('Enter a result value for every parameter');
    }

    setSaving(true);
    try {
      const payload = { ...resultForm };
      if (resultForm.ccv_result_id) {
        await ccvAPI.updateResult(resultForm.ccv_result_id, payload);
        toast.success('CCV result updated');
      } else {
        await ccvAPI.createResult(payload);
        toast.success('CCV result created');
      }
      await refreshResults();
      setResultModalOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save CCV result');
    } finally {
      setSaving(false);
    }
  };

  // ── E-sign (shared by plans and results) ──────────────────────────────────
  const openESign = (kind, mode, row) => {
    if (mode === 'returnToDraft' && row?.status === 'Approved' && !pendingChangeRef) {
      setChangeRefGate({ kind, row });
      return;
    }
    setEsign({ kind, mode, row, decision: 'approve', password: '', comment: '' });
  };
  const closeESign = () => { setEsign(null); setEsigning(false); };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      const { kind, row } = changeRefGate;
      setChangeRefGate(null);
      setEsign({ kind, mode: 'returnToDraft', row, decision: 'approve', password: '', comment: '' });
    } finally {
      setChangeRefBusy(false);
    }
  };

  const confirmESign = async () => {
    if (!esign?.password) return toast.error('Password required');
    if (esign.mode === 'returnToDraft' && !esign.comment.trim()) return toast.error('Comment required');

    const isPlan = esign.kind === 'plan';
    const id = isPlan ? esign.row.ccv_plan_id : esign.row.ccv_result_id;
    const api = {
      submit: isPlan ? ccvAPI.submit : ccvAPI.submitResult,
      approve: isPlan ? ccvAPI.approve : ccvAPI.approveResult,
      returnToDraft: isPlan ? ccvAPI.returnToDraft : ccvAPI.returnResultToDraft,
      delete: isPlan ? ccvAPI.delete : ccvAPI.deleteResult
    };

    setEsigning(true);
    try {
      if (esign.mode === 'submit') {
        await api.submit(id, { password: esign.password });
        toast.success('Submitted for approval');
      } else if (esign.mode === 'returnToDraft') {
        const payload = { comment: esign.comment, password: esign.password };
        if (esign.row?.status === 'Approved' && pendingChangeRef) {
          payload.change_reference_type = pendingChangeRef.reference_type;
          payload.change_reference_number = pendingChangeRef.reference_number;
          payload.change_reference_files = pendingChangeRef.reference_files || [];
        }
        await api.returnToDraft(id, payload);
        setPendingChangeRef(null);
        toast.success('Returned to Draft');
      } else if (esign.mode === 'delete') {
        await api.delete(id, { password: esign.password });
        toast.success(isPlan ? 'CCV plan deactivated' : 'CCV result deactivated');
      } else {
        await api.approve(id, { stage: esign.mode, decision: esign.decision, comment: esign.comment, password: esign.password });
        const label = esign.decision === 'approve' ? 'Approved' : esign.decision === 'return' ? 'Returned' : 'Rejected';
        toast.success(`${esign.mode === 'product' ? 'Product' : 'QA'} review: ${label}`);
      }
      closeESign();
      await Promise.all([refreshPlans(), refreshResults()]);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Action failed');
      setEsigning(false);
    }
  };

  const esignTitle = esign && ({
    submit: `Submit ${esign.kind === 'plan' ? 'Plan' : 'Result'} for Approval`,
    product: `${(esign.kind === 'plan' ? planRights : resultRights).earlierLabel} — Electronic Signature`,
    qa: `${(esign.kind === 'plan' ? planRights : resultRights).finalLabel} — Electronic Signature`,
    returnToDraft: 'Return to Draft — Electronic Signature',
    delete: 'Deactivate Record — Electronic Signature'
  })[esign.mode];

  // ── Row action buttons (shared shape for plans and results) ───────────────
  const RowActions = ({ kind, row, onEdit }) => {
    const status = row.status || 'Draft';
    const approvalRequired = kind === 'plan' ? planApprovalRequired : resultApprovalRequired;
    const editable = canEditRecord(row, approvalRequired);
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
        {status === 'Pending Approval' && (kind === 'plan' ? planRights : resultRights).canEarlier && (
          <button className="btn btn-sm btn-success" type="button" onClick={() => openESign(kind, 'product', row)} title={(kind === 'plan' ? planRights : resultRights).earlierLabel}>
            <FaCheck />
          </button>
        )}
        {status === 'Pending QA Approval' && (kind === 'plan' ? planRights : resultRights).canFinal && (
          <button className="btn btn-sm btn-success" type="button" onClick={() => openESign(kind, 'qa', row)} title={(kind === 'plan' ? planRights : resultRights).finalLabel}>
            <FaCheck />
          </button>
        )}
        <ActionsMenu items={[
          { label: 'Edit', icon: <FaEdit />, onClick: onEdit, show: editable },
          { label: 'Submit for Approval', icon: <FaFileUpload />, onClick: () => openESign(kind, 'submit', row), show: approvalRequired && editable && status !== 'Approved' },
          { label: 'Return to Draft', icon: <FaUndo />, onClick: () => openESign(kind, 'returnToDraft', row), show: isAdmin && status && status !== 'Draft' },
          { label: 'Deactivate', icon: <FaTrashAlt />, onClick: () => openESign(kind, 'delete', row), show: isAdmin, danger: true },
        ]} />
      </div>
    );
  };

  // ── Trend chart data — interactive: pick plan, then pick which parameter to trend ─────────
  const trendPlan = plans.find((p) => String(p.ccv_plan_id) === String(trendPlanId)) || null;
  const trendCriterion = (trendPlan?.criteria || []).find((c) => String(c.ccv_criterion_id) === String(trendCriterionId)) || trendPlan?.criteria?.[0] || null;

  const handleTrendPlanChange = (planId) => {
    const plan = plans.find((p) => String(p.ccv_plan_id) === String(planId));
    setTrendPlanId(planId);
    setTrendCriterionId(String(plan?.criteria?.[0]?.ccv_criterion_id || ''));
  };

  const trendData = useMemo(() => {
    if (!trendPlan || !trendCriterion) return [];
    return results
      .filter((r) => String(r.ccv_plan_id) === String(trendPlan.ccv_plan_id) && r.sample_date)
      .map((r) => ({ r, v: (r.values || []).find((v) => String(v.ccv_criterion_id) === String(trendCriterion.ccv_criterion_id)) }))
      .filter(({ v }) => v)
      .sort((a, b) => new Date(a.r.sample_date) - new Date(b.r.sample_date))
      .map(({ r, v }) => ({
        date: new Date(r.sample_date).toLocaleDateString(),
        batch: r.batch_number || '',
        value: Number(v.result_value),
        status: v.result_status,
        trend: v.trend_status
      }));
  }, [results, trendPlan, trendCriterion]);

  const TrendDot = (props) => {
    const { cx, cy, payload } = props;
    const style = RESULT_STATUS_STYLE[payload.status] || RESULT_STATUS_STYLE.PASS;
    return <circle cx={cx} cy={cy} r={5} fill={style.color} stroke="var(--card-bg)" strokeWidth={2} />;
  };

  const TrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '8px 12px', fontSize: 'var(--fs-sm)' }}>
        <div style={{ fontWeight: 700, marginBottom: '4px' }}>{label}{point.batch ? ` — Batch ${point.batch}` : ''}</div>
        <div>Result: {point.value} {trendCriterion?.limit_unit || ''}</div>
        <div>Status: {point.status || '-'}</div>
        <div>Trend: {point.trend || '-'}</div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h2 className="page-title">CCV — Continued Cleaning Verification</h2>
          <p className="page-subtitle">
            Define periodic monitoring plans per equipment with one or more parameters (TOC, pH, Conductivity...), log verification results against each parameter's acceptance limit, and track pass/fail trends over time.
          </p>
        </div>
        <button className="btn btn-outline btn-sm" type="button" data-module="ai_assistant" onClick={() => ai.run('AI Draft Report — CCV Trend', () => aiAPI.draftReport({ reportType: 'ccv' }), 'report')}>
          <FaRobot /> AI Draft Report
        </button>
      </div>

      {/* ── CCV Plans ──────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>CCV Plans</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
              <input className="form-control form-control-sm" style={{ paddingLeft: '30px', width: '220px' }} placeholder="Search plan, equipment, parameter..." value={planSearch} onChange={(e) => setPlanSearch(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" type="button" onClick={openNewPlan}><FaPlus /> New Plan</button>
          </div>
        </div>
        <div style={{ padding: '12px 16px 0' }}>
            <FilterStatCards groups={approvalStatusGroups} records={plans} value={planCard} onChange={setPlanCard} totalTitle="Total Plans" totalIcon={FaClipboardList} />
          </div>
          <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Plan Code</th>
                <th>Equipment</th>
                <th>Type</th>
                <th>Frequency</th>
                <th>Parameters / Limits</th>
                <th>Compliance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlans.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-muted">No CCV plans found</td></tr>
              ) : paginatedPlans.map((plan) => {
                const compliance = complianceStatusFor(plan, results);
                return (
                  <tr key={plan.ccv_plan_id}>
                    <td>
                      <span className="record-link" onClick={() => openEditPlan(plan)} title="View/edit this CCV plan" style={{ fontWeight: 600 }}>
                        {plan.plan_code}
                      </span>
                    </td>
                    <td>
                      {plan.equipment_id ? (
                        <Link to="/equipment-master" state={{ openViewEquipmentId: plan.equipment_id }} className="record-link" title="View equipment">
                          {equipmentLabel(plan.equipment_id)}
                        </Link>
                      ) : equipmentLabel(plan.equipment_id)}
                      {Array.isArray(plan.required_locations) && plan.required_locations.length > 0 && (
                        <div title={plan.required_locations_rationale || ''} style={{ fontSize: 'var(--fs-xs)', color: 'var(--warning-text)', marginTop: '2px' }}>
                          Limited to {plan.required_locations.length} location(s)
                        </div>
                      )}
                    </td>
                    <td>{plan.plan_type}</td>
                    <td>{plan.frequency_value || '-'} {plan.frequency_unit || ''}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {(plan.criteria || []).length === 0 ? '-' : plan.criteria.map((c) => (
                          <span key={c.ccv_criterion_id} style={{ fontSize: 'var(--fs-xs)', padding: '2px 8px', borderRadius: '6px', background: 'var(--surface-light)', border: '1px solid var(--border-color)' }}>
                            {c.criterion_label} {c.limit_value != null ? `≤ ${fmtNum(c.limit_value)} ${c.limit_unit || ''}` : ''}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td><Pill label={compliance} style={COMPLIANCE_STYLE[compliance]} /></td>
                    <td><StatusBadge status={plan.status || 'Draft'} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <RowActions kind="plan" row={plan} onEdit={() => openEditPlan(plan)} />
                        {plan.status === 'Approved' && (
                          <button className="btn btn-sm btn-outline" type="button" onClick={() => openNewResult(plan.ccv_plan_id)} title="Log a result for this plan">
                            <FaPlus /> Result
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredPlans.length > 0 && (
          <TablePagination total={filteredPlans.length} page={planPage} pageSize={planPageSize} onPageChange={setPlanPage} onPageSizeChange={(n) => { setPlanPageSize(n); setPlanPage(1); }} itemLabel="plans" />
        )}
      </div>

      {/* ── CCV Results ────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>CCV Results</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
              <input className="form-control form-control-sm" style={{ paddingLeft: '30px', width: '190px' }} placeholder="Search batch, location..." value={resultsSearch} onChange={(e) => setResultsSearch(e.target.value)} />
            </div>
            <select className="form-control form-control-sm" style={{ width: '220px' }} value={resultsPlanFilter} onChange={(e) => setResultsPlanFilter(e.target.value)}>
              <option value="">All plans</option>
              {plans.map((p) => <option key={p.ccv_plan_id} value={p.ccv_plan_id}>{p.plan_code} - {equipmentLabel(p.equipment_id)}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" type="button" onClick={() => openNewResult(resultsPlanFilter)}><FaPlus /> New Result</button>
            <button className="btn btn-success btn-sm" type="button" onClick={exportResultsToExcel}><FaDownload /> Excel Export</button>
          </div>
        </div>
        <div style={{ padding: '12px 16px 0' }}>
            <FilterStatCards groups={approvalStatusGroups} records={results} value={resultCard} onChange={setResultCard} totalTitle="Total Results" totalIcon={FaFileAlt} />
          </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Plan / Equipment</th>
                <th>Location / Part</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('batch_number')}>Batch{sortIndicator('batch_number')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('sample_date')}>Sample Date{sortIndicator('sample_date')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('result_date')}>Result Date{sortIndicator('result_date')}</th>
                <th>Results vs Limits</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Overall{sortIndicator('status')}</th>
                <th>Attachment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.length === 0 ? (
                <tr><td colSpan={10} className="text-center text-muted">No CCV results found</td></tr>
              ) : paginatedResults.map((result) => (
                <tr key={result.ccv_result_id}>
                  <td>
                    {result.ccv_plan_id ? (
                      <span className="record-link" onClick={() => { const p = plans.find((pl) => String(pl.ccv_plan_id) === String(result.ccv_plan_id)); if (p) openEditPlan(p); }} title="View/edit this CCV plan">{result.plan_code}</span>
                    ) : result.plan_code}
                    <br /><span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>{result.equipment_name || '-'}</span>
                  </td>
                  <td>{result.location_name || <span style={{ color: 'var(--text-secondary)' }}>-</span>}<br /><span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>{result.location_code || ''}</span></td>
                  <td>{result.batch_number || '-'}</td>
                  <td>{result.sample_date ? new Date(result.sample_date).toLocaleDateString() : '-'}</td>
                  <td>{result.result_date ? new Date(result.result_date).toLocaleDateString() : '-'}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {(result.values || []).map((v) => (
                        <div key={v.ccv_result_value_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--fs-sm)' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>{v.criterion_label}:</span>
                          <span>{fmtNum(v.result_value)} / {fmtNum(v.limit_value)} {v.limit_unit || ''}</span>
                          {v.result_status && <Pill label={v.result_status} style={RESULT_STATUS_STYLE[v.result_status]} />}
                        </div>
                      ))}
                      {!(result.values || []).length && <span style={{ color: 'var(--text-secondary)' }}>-</span>}
                    </div>
                  </td>
                  <td>{overallStatus(result.values) !== '-' ? <Pill label={overallStatus(result.values)} style={RESULT_STATUS_STYLE[overallStatus(result.values)]} /> : '-'}</td>
                  <td>
                    {result.attachment_name ? (
                      <a href={result.attachment_data} download={result.attachment_name} title={result.attachment_name} style={{ fontSize: 'var(--fs-sm)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <FaPaperclip /> {result.attachment_name.length > 14 ? `${result.attachment_name.slice(0, 12)}…` : result.attachment_name}
                      </a>
                    ) : '-'}
                  </td>
                  <td><StatusBadge status={result.status || 'Draft'} /></td>
                  <td><RowActions kind="result" row={result} onEdit={() => openEditResult(result)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredResults.length > 0 && (
          <TablePagination total={filteredResults.length} page={resultPage} pageSize={resultPageSize} onPageChange={setResultPage} onPageSizeChange={(n) => { setResultPageSize(n); setResultPage(1); }} itemLabel="results" />
        )}
      </div>

      {/* ── Trend Analysis ─────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)', display: 'flex', alignItems: 'center', gap: '8px' }}><FaChartLine /> Trend Analysis</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <select className="form-control form-control-sm" style={{ width: '230px' }} value={trendPlanId} onChange={(e) => handleTrendPlanChange(e.target.value)}>
              {plans.length === 0 && <option value="">No plans yet</option>}
              {plans.map((p) => <option key={p.ccv_plan_id} value={p.ccv_plan_id}>{p.plan_code} - {equipmentLabel(p.equipment_id)}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {(trendPlan?.criteria || []).map((c) => (
                <button
                  key={c.ccv_criterion_id}
                  type="button"
                  onClick={() => setTrendCriterionId(String(c.ccv_criterion_id))}
                  className="btn btn-sm"
                  style={{
                    padding: '4px 10px', fontSize: 'var(--fs-sm)', borderRadius: '999px',
                    border: `1px solid ${String(c.ccv_criterion_id) === String(trendCriterionId) ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    background: String(c.ccv_criterion_id) === String(trendCriterionId) ? 'var(--primary-color)' : 'transparent',
                    color: String(c.ccv_criterion_id) === String(trendCriterionId) ? '#fff' : 'var(--text-secondary)'
                  }}
                >
                  {c.criterion_label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ padding: '16px' }}>
          {trendData.length === 0 ? (
            <div className="text-center text-muted" style={{ padding: '30px 0' }}>No results logged yet for this parameter</div>
          ) : (
            <>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="date" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                    <YAxis
                      tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
                      domain={[0, (dataMax) => {
                        const limit = Number(trendCriterion?.limit_value);
                        const ceiling = Number.isFinite(limit) ? Math.max(dataMax, limit) : dataMax;
                        return Math.ceil(ceiling * 1.15);
                      }]}
                    />
                    <Tooltip content={<TrendTooltip />} />
                    {trendCriterion?.limit_value != null && (
                      <ReferenceLine
                        y={Number(trendCriterion.limit_value)}
                        stroke="var(--danger-color)"
                        strokeDasharray="4 4"
                        label={{ value: `Limit (${fmtNum(trendCriterion.limit_value)} ${trendCriterion.limit_unit || ''})`, fill: 'var(--danger-color)', fontSize: 11, position: 'insideTopRight' }}
                      />
                    )}
                    <Line type="monotone" dataKey="value" stroke="var(--primary-color)" strokeWidth={2} dot={<TrendDot />} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: 'var(--success-text)', marginRight: '6px' }} />Pass</span>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: 'var(--danger-text)', marginRight: '6px' }} />Fail</span>
                <span><span style={{ display: 'inline-block', width: '14px', borderTop: '2px dashed var(--danger-color)', marginRight: '6px', verticalAlign: 'middle' }} />Acceptance Limit</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Plan Modal ─────────────────────────────────────────────────────── */}
      <Modal
        isOpen={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        title={planForm.ccv_plan_id ? 'Edit CCV Plan' : 'New CCV Plan'}
        size="lg"
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setPlanModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={savePlan} disabled={saving}>{saving ? 'Saving...' : 'Save Plan'}</button>
          </>
        }
      >
        <div className="modal-section">
          <h3 className="modal-section-title">Plan Details</h3>
          <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Plan Code *</label>
            <input className="form-control" value={planForm.plan_code} onChange={(e) => setPlanField('plan_code', e.target.value)} placeholder="e.g. CCV-001" />
          </div>
          <div className="form-group">
            <label className="form-label">Equipment *</label>
            <select className="form-control" value={planForm.equipment_id} onChange={(e) => setPlanField('equipment_id', e.target.value)}>
              <option value="">Select equipment</option>
              {equipment.map((eq) => <option key={eq.equipment_id} value={eq.equipment_id}>{eq.equipment_code} - {eq.equipment_name}</option>)}
            </select>
          </div>
          {planForm.equipment_id && planEquipmentLocations.length > 0 && (
            <div className="form-group form-group-wide">
              <label className="form-label">Locations sampled each cycle</label>
              <p style={{ margin: '0 0 6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                Leave all unchecked to allow any of this equipment's sampling locations. Check a subset to limit — and document why — this cycle's sampling to only those locations.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '140px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px' }}>
                {planEquipmentLocations.map((loc) => (
                  <label key={loc.location_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--fs-sm)' }}>
                    <input type="checkbox" checked={(planForm.required_locations || []).includes(String(loc.location_id))}
                      onChange={() => togglePlanRequiredLocation(loc.location_id)} />
                    {loc.location_code} - {loc.location_name} <span style={{ color: 'var(--text-secondary)' }}>({loc.plan_code})</span>
                  </label>
                ))}
              </div>
              {(planForm.required_locations || []).length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <label className="form-label" style={{ color: 'var(--warning-text)' }}>
                    Rationale for limiting to {(planForm.required_locations || []).length} of {planEquipmentLocations.length} location(s) (required)
                  </label>
                  <textarea className="form-control" rows={2} value={planForm.required_locations_rationale || ''}
                    onChange={(e) => setPlanField('required_locations_rationale', e.target.value)}
                    placeholder="Explain why this cycle doesn't need to sample every location..." />
                </div>
              )}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Plan Type</label>
            <select className="form-control" value={planForm.plan_type} onChange={(e) => setPlanField('plan_type', e.target.value)}>
              <option value="Batch-based">Batch-based</option>
              <option value="Time-based">Time-based</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Frequency</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input className="form-control" type="number" min="1" value={planForm.frequency_value} onChange={(e) => setPlanField('frequency_value', e.target.value)} style={{ flex: 1 }} />
              <select className="form-control" value={planForm.frequency_unit} onChange={(e) => setPlanField('frequency_unit', e.target.value)} style={{ flex: 1 }}>
                {FREQUENCY_UNITS_BY_TYPE[planForm.plan_type].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          </div>
        </div>

        <div className="modal-section">
          <h3 className="modal-section-title">Parameters &amp; Limits</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label className="form-label" style={{ margin: 0 }}>Parameters &amp; Acceptance Limits *</label>
            <button className="btn btn-outline btn-sm" type="button" onClick={addCriterionRow}>+ Add Parameter</button>
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
            Every sample logged against this plan will ask for a result against each parameter below (NMT — result ≤ limit = PASS).
          </p>
          {planForm.criteria.map((criterion, index) => (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 110px 100px 32px', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <select className="form-control form-control-sm" onChange={(e) => applyPreset(index, e.target.value)} defaultValue="">
                <option value="" disabled>Preset...</option>
                {PARAMETER_PRESETS.map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
              </select>
              <input className="form-control form-control-sm" placeholder="Parameter name (e.g. pH)" value={criterion.criterion_label} onChange={(e) => setCriterionField(index, 'criterion_label', e.target.value)} />
              <input className="form-control form-control-sm" type="number" step="any" placeholder="Limit" value={criterion.limit_value} onChange={(e) => setCriterionField(index, 'limit_value', e.target.value)} />
              <input className="form-control form-control-sm" placeholder="Unit" value={criterion.limit_unit} onChange={(e) => setCriterionField(index, 'limit_unit', e.target.value)} />
              <button type="button" className="btn btn-sm btn-danger-outline" onClick={() => removeCriterionRow(index)} disabled={planForm.criteria.length === 1} title="Remove"><FaTimes /></button>
            </div>
          ))}
        </div>

        <div className="modal-section">
          <h3 className="modal-section-title">Remarks</h3>
          <div className="form-group">
            <label className="form-label">Remarks</label>
            <textarea className="form-control" rows={2} value={planForm.remarks} onChange={(e) => setPlanField('remarks', e.target.value)} />
          </div>
        </div>
      </Modal>

      {/* ── Result Modal ───────────────────────────────────────────────────── */}
      <Modal
        isOpen={resultModalOpen}
        onClose={() => setResultModalOpen(false)}
        title={resultForm.ccv_result_id ? 'Edit CCV Result' : 'New CCV Result'}
        size="lg"
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setResultModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveResult} disabled={saving}>{saving ? 'Saving...' : 'Save Result'}</button>
          </>
        }
      >
        <div className="modal-section">
          <h3 className="modal-section-title">Sample Details</h3>
          <div className="form-grid">
          <div className="form-group">
            <label className="form-label">CCV Plan *</label>
            <select className="form-control" value={resultForm.ccv_plan_id} onChange={(e) => handleResultPlanChange(e.target.value)}>
              <option value="">Select plan</option>
              {plans.map((p) => <option key={p.ccv_plan_id} value={p.ccv_plan_id}>{p.plan_code} - {equipmentLabel(p.equipment_id)} ({p.status || 'Draft'})</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Location / Part Sampled</label>
            <select className="form-control" value={resultForm.sampling_location_id} onChange={(e) => setResultField('sampling_location_id', e.target.value)} disabled={!resultPlan}>
              <option value="">{resultPlan ? 'Select location (optional)' : 'Select a plan first'}</option>
              {resultLocationOptions.map((loc) => (
                <option key={loc.location_id} value={loc.location_id}>{loc.location_code} - {loc.location_name} ({loc.plan_code})</option>
              ))}
            </select>
            {resultPlan && !resultLocationOptions.length && (
              <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>No approved Sampling Plan locations found for this equipment yet.</p>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Batch Number</label>
            <input className="form-control" value={resultForm.batch_number} onChange={(e) => setResultField('batch_number', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Sample Date *</label>
            <input className="form-control" type="date" value={resultForm.sample_date} onChange={(e) => setResultField('sample_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Result Date</label>
            <input className="form-control" type="date" value={resultForm.result_date} onChange={(e) => setResultField('result_date', e.target.value)} />
          </div>
          </div>
        </div>

        <div className="modal-section">
          <h3 className="modal-section-title">Results &amp; Attachments</h3>
          <label className="form-label">Results</label>
          {!resultForm.values.length ? (
            <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Select a plan to see its parameters.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {resultForm.values.map((value) => {
                const criterion = (resultPlan?.criteria || []).find((c) => String(c.ccv_criterion_id) === String(value.ccv_criterion_id));
                const preview = previewResultStatus(value.result_value, criterion?.limit_value);
                return (
                  <div key={value.ccv_criterion_id} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '10px', alignItems: 'center' }}>
                    <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{criterion?.criterion_label || '-'}</span>
                    <div>
                      <input
                        className="form-control"
                        type="number"
                        step="any"
                        value={value.result_value}
                        onChange={(e) => setResultValueField(value.ccv_criterion_id, e.target.value)}
                      />
                      <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                        Acceptance Criteria — Limit: {fmtNum(criterion?.limit_value)} {criterion?.limit_unit || ''} (NMT).{' '}
                        {value.result_value !== '' && preview && (
                          <strong style={{ color: preview === 'PASS' ? 'var(--success-text)' : 'var(--danger-text)' }}>Preview: {preview}</strong>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-section">
          <h3 className="modal-section-title">Comments &amp; Attachments</h3>
          <div className="form-group">
          <label className="form-label">Comments</label>
          <textarea className="form-control" rows={2} value={resultForm.comments} onChange={(e) => setResultField('comments', e.target.value)} />
          </div>

          <div className="form-group">
          <label className="form-label">Raw Data Attachment</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="file" onChange={(e) => handleAttachmentChange(e.target.files?.[0])} disabled={attachmentUploading} />
            {resultForm.attachment_name && (
              <span style={{ fontSize: 'var(--fs-sm)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <FaPaperclip /> {resultForm.attachment_name}
                <button type="button" className="btn btn-sm btn-danger-outline" onClick={() => setResultForm((p) => ({ ...p, attachment_name: '', attachment_data: '' }))}><FaTimes /></button>
              </span>
            )}
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Attach the instrument printout / raw data file backing this result (pH meter, conductivity meter, TOC analyzer, etc.).</p>
          </div>
        </div>
      </Modal>

      <ChangeReferenceModal
        open={!!changeRefGate}
        qmsLinked={qmsLinked}
        busy={changeRefBusy}
        recordLabel={changeRefGate?.row?.ccv_code || changeRefGate?.row?.equipment_name}
        onCancel={() => setChangeRefGate(null)}
        onConfirm={confirmChangeReference}
      />

      {/* ── E-sign Modal ───────────────────────────────────────────────────── */}
      {esign && (
        <div className="modal-overlay" style={{ backdropFilter: 'blur(3px)', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeESign(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', width: '95%' }}>
            <div className="modal-header">
              <h2 className="modal-title" style={{ fontSize: 'var(--fs-xl)' }}>{esignTitle}</h2>
              <button className="modal-close" onClick={closeESign}>×</button>
            </div>
            <div className="modal-body">
              {(esign.mode === 'product' || esign.mode === 'qa') && (
                <div className="form-group">
                  <label className="form-label">Decision *</label>
                  <select className="form-control" value={esign.decision} onChange={(e) => setEsign((p) => ({ ...p, decision: e.target.value }))}>
                    <option value="approve">Approve</option>
                    <option value="return">Return to Draft</option>
                    <option value="reject">Reject</option>
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Login Password *</label>
                <input type="password" className="form-control" autoComplete="new-password"
                  value={esign.password} onChange={(e) => setEsign((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Enter your login password"
                  onKeyDown={(e) => { if (e.key === 'Enter') confirmESign(); }} />
              </div>
              <div className="form-group">
                <label className="form-label">Reason / Comment {esign.mode === 'returnToDraft' ? '*' : ''}</label>
                <textarea className="form-control" rows={3} value={esign.comment} onChange={(e) => setEsign((p) => ({ ...p, comment: e.target.value }))} placeholder="Provide reason or comment..." />
              </div>
              <div style={{ padding: '8px 12px', background: 'var(--bg-color)', borderRadius: '6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
                <strong>Signed by:</strong> {user.username || 'Unknown'} &nbsp;|&nbsp;
                <strong>Date/Time:</strong> {new Date().toLocaleString()} &nbsp;|&nbsp;
                <strong>Role:</strong> {user.role}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
              <button className="btn btn-outline" onClick={closeESign} disabled={esigning}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmESign} disabled={esigning}>
                {esigning ? 'Processing...' : (
                  esign.mode === 'submit'        ? 'Confirm & Submit' :
                  esign.mode === 'returnToDraft' ? 'Confirm Return to Draft' :
                  esign.mode === 'delete'        ? 'Confirm Deactivate' :
                  esign.mode === 'product'       ? 'Confirm Product Decision' :
                                                    'Confirm QA Decision'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        docType="report"
        exportFileName="ccv-ai-report.pdf"
      />
    </div>
  );
};

export default CCV;
